import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const DEVICE_STATUS_OPTIONS = [
  { value: "connected", label: "Connected" },
  { value: "disconnected", label: "Disconnected" },
  { value: "no data", label: "No Data" },
];

export const DEVICE_HAS_IP_OPTIONS = [
  { value: "true", label: "True" },
  { value: "false", label: "False" },
];

export const DEVICE_CHARGING_OPTIONS = [
  { value: "true", label: "True" },
  { value: "false", label: "False" },
  { value: "no data", label: "No Data" },
];

export const DEVICE_IN_KIOSK_OPTIONS = [
  { value: "true", label: "True" },
  { value: "false", label: "False" },
];

export const ColumnFilterControl = ({
  label,
  isOpen,
  onToggle,
  onClose,
  isActive,
  children,
}) => {
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!isOpen) return undefined;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const panelWidth = 288; // w-72
      const margin = 8;
      const maxLeft = Math.max(margin, window.innerWidth - panelWidth - margin);
      const left = Math.min(Math.max(rect.left, margin), maxLeft);
      const top = rect.bottom + margin;

      setPanelPosition({ top, left });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closePanel = () => {
      if (typeof onClose === "function") {
        onClose();
        return;
      }
      onToggle();
    };

    const handlePointerDownOutside = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      closePanel();
    };

    document.addEventListener("mousedown", handlePointerDownOutside);
    document.addEventListener("touchstart", handlePointerDownOutside);
    return () => {
      document.removeEventListener("mousedown", handlePointerDownOutside);
      document.removeEventListener("touchstart", handlePointerDownOutside);
    };
  }, [isOpen, onClose, onToggle]);

  return (
    <div className="relative inline-flex items-center gap-1">
      <span>{label}</span>
      <button
        ref={triggerRef}
        type="button"
        className={`inline-flex items-center justify-center h-6 w-6 rounded transition-colors ${isActive
            ? "bg-red-100 text-red-600"
            : "text-on-surface-variant hover:bg-surface-container-high hover:text-primary"
          }`}
        onClick={onToggle}
        title={`Filter ${label}`}
        aria-label={`Filter ${label}`}
      >
        <span className="material-symbols-outlined text-sm">filter_alt</span>
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed w-72 z-[200] p-3 rounded-xl shadow-xl bg-surface-container-lowest border border-outline-variant/30 space-y-3"
            style={{ top: `${panelPosition.top}px`, left: `${panelPosition.left}px` }}
          >
            {children}
          </div>,
          document.body
        )}
    </div>
  );
};

export const ColumnSortButton = ({ isActive, direction, onClick, label }) => (
  <button
    type="button"
    className={`inline-flex items-center justify-center h-6 w-6 rounded transition-colors ${isActive
        ? "bg-primary/20 text-primary"
        : "text-on-surface-variant hover:bg-surface-container-high hover:text-primary"
      }`}
    onClick={onClick}
    title={`Sort ${label}`}
    aria-label={`Sort ${label}`}
  >
    <span className="material-symbols-outlined text-sm">
      {isActive ? (direction === "asc" ? "arrow_upward" : "arrow_downward") : "unfold_more"}
    </span>
  </button>
);

export const RangeFilterPanel = ({
  value,
  minPlaceholder = "Min",
  maxPlaceholder = "Max",
  onChange,
  onClear,
  onDone,
}) => (
  <>
    <label className="block space-y-1">
      <span className="text-[11px] text-on-surface-variant">Between</span>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          className="w-full px-2 py-1.5 rounded-md bg-surface-container-high text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary/20"
          placeholder={minPlaceholder}
          value={value?.min ?? ""}
          onChange={(e) => onChange("min", e.target.value)}
        />
        <input
          type="number"
          className="w-full px-2 py-1.5 rounded-md bg-surface-container-high text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary/20"
          placeholder={maxPlaceholder}
          value={value?.max ?? ""}
          onChange={(e) => onChange("max", e.target.value)}
        />
      </div>
    </label>
    <div className="flex justify-end gap-2 pt-1">
      <button
        type="button"
        className="px-2.5 py-1.5 text-xs rounded-md bg-surface-container-high hover:bg-surface-container-highest"
        onClick={onClear}
      >
        Clear
      </button>
      <button
        type="button"
        className="px-2.5 py-1.5 text-xs rounded-md bg-primary text-white hover:opacity-90"
        onClick={onDone}
      >
        Done
      </button>
    </div>
  </>
);

export const DateRangeFilterPanel = ({ value, onChange, onClear, onDone }) => (
  <>
    <label className="block space-y-1">
      <span className="text-[11px] text-on-surface-variant">Start / End date</span>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="date"
          className="w-full px-2 py-1.5 rounded-md bg-surface-container-high text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary/20"
          value={value?.start ?? ""}
          onChange={(e) => onChange("start", e.target.value)}
        />
        <input
          type="date"
          className="w-full px-2 py-1.5 rounded-md bg-surface-container-high text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary/20"
          value={value?.end ?? ""}
          onChange={(e) => onChange("end", e.target.value)}
        />
      </div>
    </label>
    <div className="flex justify-end gap-2 pt-1">
      <button
        type="button"
        className="px-2.5 py-1.5 text-xs rounded-md bg-surface-container-high hover:bg-surface-container-highest"
        onClick={onClear}
      >
        Clear
      </button>
      <button
        type="button"
        className="px-2.5 py-1.5 text-xs rounded-md bg-primary text-white hover:opacity-90"
        onClick={onDone}
      >
        Done
      </button>
    </div>
  </>
);

export const DeviceSearchBar = ({
  value,
  onChange,
  placeholder = "Search by device name or ID",
  isActive = false,
}) => (
  <div
    className={`flex items-center gap-2 w-full max-w-sm px-3 py-1.5 rounded-lg border transition-colors ${isActive
        ? "border-primary/40 bg-primary/5"
        : "border-outline-variant/20 bg-surface-container-low"
      }`}
  >
    <span className="material-symbols-outlined text-base text-on-surface-variant">search</span>
    <input
      type="text"
      className="flex-1 min-w-0 bg-transparent text-xs text-on-surface outline-none placeholder:text-on-surface-variant"
      placeholder={placeholder}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Search devices"
    />
    {String(value || "").trim() !== "" && (
      <button
        type="button"
        className="inline-flex items-center justify-center h-5 w-5 rounded text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface transition-colors"
        onClick={() => onChange("")}
        title="Clear search"
        aria-label="Clear search"
      >
        <span className="material-symbols-outlined text-sm">close</span>
      </button>
    )}
  </div>
);

export const SearchFilterPanel = ({ value, onChange, placeholder, onClear, onDone }) => (
  <>
    <label className="block space-y-1">
      <span className="text-[11px] text-on-surface-variant">Contains</span>
      <input
        type="text"
        className="w-full px-2 py-1.5 rounded-md bg-surface-container-high text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary/20"
        placeholder={placeholder}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
    <div className="flex justify-end gap-2 pt-1">
      <button
        type="button"
        className="px-2.5 py-1.5 text-xs rounded-md bg-surface-container-high hover:bg-surface-container-highest"
        onClick={onClear}
      >
        Clear
      </button>
      <button
        type="button"
        className="px-2.5 py-1.5 text-xs rounded-md bg-primary text-white hover:opacity-90"
        onClick={onDone}
      >
        Done
      </button>
    </div>
  </>
);

export const DropdownFilterPanel = ({
  value,
  options,
  onChange,
  onClear,
  onDone,
}) => (
  <>
    <label className="block space-y-1">
      <span className="text-[11px] text-on-surface-variant">Choose one or more</span>
      <select
        multiple
        className="w-full px-2 py-1.5 rounded-md bg-surface-container-high text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary/20 min-h-[90px]"
        value={Array.isArray(value) ? value : []}
        onChange={(e) =>
          onChange(Array.from(e.target.selectedOptions).map((option) => option.value))
        }
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
    <div className="flex justify-end gap-2 pt-1">
      <button
        type="button"
        className="px-2.5 py-1.5 text-xs rounded-md bg-surface-container-high hover:bg-surface-container-highest"
        onClick={onClear}
      >
        Clear
      </button>
      <button
        type="button"
        className="px-2.5 py-1.5 text-xs rounded-md bg-primary text-white hover:opacity-90"
        onClick={onDone}
      >
        Done
      </button>
    </div>
  </>
);
